import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import { ArrowPathIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export default function ResetJourneyModal({
  isOpen,
  onClose,
  onConfirm,
  candidateName,
  eligibilityData,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  candidateName: string;
  eligibilityData: any;
}) {
  const [isResetting, setIsResetting] = useState(false);

  const handleConfirm = async () => {
    setIsResetting(true);
    try {
      await onConfirm();
    } finally {
      setIsResetting(false);
      onClose();
    }
  };

  // Reset is permitted from any stage, so this text is context for the recruiter about what is
  // about to be destroyed - it never blocks the action. eligibilityData may be null if the
  // pre-check request failed; the reset still goes ahead, just without the extra detail.
  const getReasonText = () => {
    if (eligibilityData?.reason === "TestCompleted") {
      return "This candidate has already completed an assessment test for this role.";
    }
    if (eligibilityData?.reason === "SlotBooked") {
      return "This candidate has a booked assessment slot but hasn't completed the test.";
    }
    return "This candidate has no assessment booking or score on record for this role.";
  };

  const currentStage = eligibilityData?.currentRoleStatusName;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-[#1f2937] px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900 sm:mx-0 sm:h-10 sm:w-10">
                    <ArrowPathIcon
                      className="h-6 w-6 text-green-600 dark:text-green-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100"
                    >
                      Reset Candidate
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-300">
                        Are you sure you want to reset the journey for{" "}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {candidateName}
                        </span>
                        ?
                      </p>

                      <div className="mt-4 rounded-md bg-yellow-50 dark:bg-yellow-900/30 p-4">
                        <div className="flex">
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                              Warning
                            </h3>
                            <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                              {/* Naming the stage matters more now that a reset is allowed from
                                  anywhere - it is the recruiter's last chance to notice they are
                                  about to wipe a candidate who has moved well past assessments. */}
                              {currentStage && (
                                <p className="mb-2">
                                  This candidate is currently at the <strong>{currentStage}</strong> stage.
                                </p>
                              )}
                              <p>{getReasonText()}</p>
                              <p className="mt-2">
                                Resetting the journey will move the candidate back to the <strong>Applied</strong> stage. All assessment bookings, scores, and stage progress for this role will be cleared permanently.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={isResetting}
                    className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleConfirm}
                  >
                    {isResetting ? (
                      <>
                        <ArrowPathIcon className="mr-2 h-5 w-5 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      "Reset Candidate"
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={isResetting}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
